/**
 * Navigation Utility Functions
 *
 * Centralized utilities for navigation state management,
 * including the "Most Specific Match Wins" algorithm for active state.
 */

import type { ResolvedNavItem, ResolvedNavGroup } from './types';

// ============================================================================
// Types
// ============================================================================

/**
 * Match type for active state detection
 */
export type ActiveMatchType = 'exact' | 'pattern' | 'prefix';

/**
 * Active match mode configuration
 * - 'exact': Only active on exact path match (default for items WITH children)
 * - 'prefix': Active on prefix match (default for leaf items)
 * - 'pattern': Use pathPattern for matching
 */
export type ActiveMatchMode = 'exact' | 'prefix' | 'pattern';

/**
 * Result of matching an item to the current path
 */
interface ActiveMatchResult {
  item: ResolvedNavItem;
  matchType: ActiveMatchType;
  specificity: number;
}

// ============================================================================
// Path Matching Utilities
// ============================================================================

/**
 * Check if a path matches a pattern (e.g., /calls/[callId])
 */
export function pathMatches(currentPath: string, pattern: string): boolean {
  if (!pattern) return false;

  const escapeRegex = (value: string) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Convert pattern to regex, escaping static segments
  const segments = pattern.split('/').map((segment) => {
    if (segment.startsWith('[...') && segment.endsWith(']')) {
      return '(.+)'; // catch-all
    }
    if (segment.startsWith('[') && segment.endsWith(']')) {
      return '([^/]+)'; // single segment
    }
    return escapeRegex(segment);
  });

  const regexPattern = segments.join('/');

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(currentPath);
}

/**
 * Calculate specificity score for a match
 * Higher score = more specific match
 */
function calculateSpecificity(
  itemPath: string,
  matchType: ActiveMatchType
): number {
  // Path depth: more segments = more specific
  const pathDepth = itemPath.split('/').filter(Boolean).length;

  // Base score from path depth (multiply by 100 to leave room for bonuses)
  let score = pathDepth * 100;

  // Bonuses based on match type
  switch (matchType) {
    case 'exact':
      score += 1000; // Exact matches always win
      break;
    case 'pattern':
      score += 500;  // Pattern matches are next priority
      break;
    case 'prefix':
      score += 0;    // Prefix matches are lowest priority
      break;
  }

  return score;
}

/**
 * Determine the default active match mode for an item
 */
function getDefaultMatchMode(item: ResolvedNavItem): ActiveMatchMode {
  // Items with children should only be active on exact match
  // (don't highlight parent when child is active)
  if (item.children && item.children.length > 0) {
    return 'exact';
  }

  // Items with pathPattern should use pattern matching
  if (item.pathPattern) {
    return 'pattern';
  }

  // Leaf items default to prefix matching
  // (catches nested dynamic routes like /adminpanel/llm-metrics/requests/123)
  return 'prefix';
}

/**
 * Check if an item matches the current path
 */
function getItemMatch(
  item: ResolvedNavItem,
  currentPath: string,
  overrideMode?: ActiveMatchMode
): { type: ActiveMatchType } | null {
  const basePath = item.path.split('?')[0];

  // Exact match - always wins if matched
  if (currentPath === basePath) {
    return { type: 'exact' };
  }

  // Determine match mode
  const mode = overrideMode ?? item.activeMatchMode ?? getDefaultMatchMode(item);

  // Pattern match (check before prefix for items with patterns)
  if ((mode === 'pattern' || mode === 'prefix') && item.pathPattern) {
    if (pathMatches(currentPath, item.pathPattern)) {
      return { type: 'pattern' };
    }
  }

  // Prefix match - only if mode allows it
  if (mode === 'prefix' && currentPath.startsWith(basePath + '/')) {
    return { type: 'prefix' };
  }

  return null;
}

// ============================================================================
// Main Active State Algorithm
// ============================================================================

/**
 * Find the single most specific active navigation item
 *
 * Algorithm: "Most Specific Match Wins"
 * 1. Collect all items that match the current path
 * 2. Calculate specificity score for each match
 * 3. Return the item with the highest specificity
 *
 * This ensures only ONE item is ever marked as active, even when
 * paths share prefixes (e.g., /adminpanel and /adminpanel/llm-metrics).
 */
export function findActiveNavItem(
  items: ResolvedNavItem[],
  currentPath: string
): ResolvedNavItem | null {
  const matches: ActiveMatchResult[] = [];

  // Recursively collect all matching items (including children)
  function collectMatches(itemList: ResolvedNavItem[]) {
    for (const item of itemList) {
      const match = getItemMatch(item, currentPath);

      if (match) {
        matches.push({
          item,
          matchType: match.type,
          specificity: calculateSpecificity(item.path, match.type),
        });
      }

      // Check children recursively
      if (item.children && item.children.length > 0) {
        collectMatches(item.children);
      }
    }
  }

  collectMatches(items);

  if (matches.length === 0) {
    return null;
  }

  // Sort by specificity descending. On a tie (a parent and a child sharing the
  // exact same path — e.g. a subgroup header pointed at its first child's
  // route), prefer the LEAF item so an active child never yields its highlight
  // to its identically-pathed parent. This upholds the "don't highlight parent
  // when child is active" intent in getDefaultMatchMode above.
  matches.sort((a, b) => {
    if (b.specificity !== a.specificity) return b.specificity - a.specificity;
    const aHasChildren = (a.item.children?.length ?? 0) > 0;
    const bHasChildren = (b.item.children?.length ?? 0) > 0;
    if (aHasChildren !== bHasChildren) return aHasChildren ? 1 : -1;
    // The invisible 'submenu' group mirrors many rail routes (e.g. both
    // training.dashboard and submenu.training.dashboard point at
    // /training/dashboard). The rail highlight must win deterministically —
    // the sub-sidebar computes its own active state — so prefer non-submenu
    // rows over their submenu mirrors on an otherwise-equal match.
    const aSub = a.item.groupKey === 'submenu';
    const bSub = b.item.groupKey === 'submenu';
    if (aSub !== bSub) return aSub ? 1 : -1;
    return 0;
  });

  // Return the most specific match
  return matches[0].item;
}

/**
 * Mark active state on navigation items based on current path
 *
 * This function mutates the items in-place for performance,
 * setting isActive: true only on the single most specific match.
 */
export function markActiveNavItems(
  items: ResolvedNavItem[],
  currentPath: string
): void {
  // First, find the most specific active item
  const activeItem = findActiveNavItem(items, currentPath);
  const activeKey = activeItem?.key ?? null;

  // Then, mark all items with their active state
  function markItems(itemList: ResolvedNavItem[]) {
    for (const item of itemList) {
      item.isActive = item.key === activeKey;

      // Recursively mark children
      if (item.children && item.children.length > 0) {
        markItems(item.children);
      }
    }
  }

  markItems(items);
}

/**
 * Mark active state on navigation groups and their items
 */
export function markActiveNavGroups(
  groups: ResolvedNavGroup[],
  currentPath: string
): void {
  // Collect all items from all groups
  const allItems: ResolvedNavItem[] = [];
  for (const group of groups) {
    allItems.push(...group.items);
  }

  // Find and mark active item
  markActiveNavItems(allItems, currentPath);
}

// ============================================================================
// Helper Utilities
// ============================================================================

/**
 * Check if any child of an item is active
 * Useful for expanding parent groups in the sidebar
 */
export function hasActiveChild(
  item: ResolvedNavItem,
  currentPath: string
): boolean {
  if (!item.children || item.children.length === 0) {
    return false;
  }

  for (const child of item.children) {
    const match = getItemMatch(child, currentPath, 'prefix');
    if (match) return true;

    // Check grandchildren
    if (hasActiveChild(child, currentPath)) {
      return true;
    }
  }

  return false;
}

/**
 * Get the active item's parent chain
 * Returns an array of item keys from root to active item
 */
export function getActiveItemChain(
  items: ResolvedNavItem[],
  currentPath: string
): string[] {
  const chain: string[] = [];

  function findChain(itemList: ResolvedNavItem[], parentChain: string[]): boolean {
    for (const item of itemList) {
      const currentChain = [...parentChain, item.key];

      // Check if this item is active
      if (item.isActive || getItemMatch(item, currentPath)?.type === 'exact') {
        chain.push(...currentChain);
        return true;
      }

      // Check children
      if (item.children && item.children.length > 0) {
        if (findChain(item.children, currentChain)) {
          return true;
        }
      }
    }

    return false;
  }

  findChain(items, []);
  return chain;
}

/**
 * Flatten a navigation tree into a single array
 */
export function flattenNavItems(items: ResolvedNavItem[]): ResolvedNavItem[] {
  const result: ResolvedNavItem[] = [];

  function flatten(itemList: ResolvedNavItem[]) {
    for (const item of itemList) {
      result.push(item);
      if (item.children && item.children.length > 0) {
        flatten(item.children);
      }
    }
  }

  flatten(items);
  return result;
}

// ============================================================================
// Client-side ACL (Defense-in-Depth)
// ============================================================================

/**
 * Role slugs that the client-side filter treats as "admin" for the purposes
 * of `auth_requirement === 'admin' | 'platform'` and `required_roles`
 * containing 'admin', 'admin-platform', 'admin-tenant', or 'platform-admin'.
 *
 * Mirrors `ADMIN_ROLE_SLUGS` from `lib/auth/role-slugs.ts` so the navigation
 * package stays free of cross-package coupling. Keep in sync.
 */
const NAV_ADMIN_ROLE_SLUGS: ReadonlySet<string> = new Set([
  'admin',
  'admin-platform',
  'admin-tenant',
  'platform-admin',
  'executive',
  'owner',
]);

/**
 * Caller-supplied access state for client-side navigation gating.
 * Resolved by the consumer (e.g. via `useIsAdmin`, `useIsStaff`).
 */
export interface NavAccessState {
  /** True if the user is authenticated. */
  isAuthenticated: boolean;
  /** True if the user is staff (officer/admin/supervisor/etc). */
  isStaff: boolean;
  /** True if the user is admin (any flavor). */
  isAdmin: boolean;
  /** True if the user is a platform-level admin. */
  isPlatformAdmin: boolean;
}

/**
 * Defense-in-depth: returns true if the navigation item/group should be
 * rendered for the given access state. The server filter
 * (`app/api/navigation/route.ts`) is authoritative — this is purely
 * cosmetic to hide entries that, in pathological cases, slip past the
 * server filter (e.g. cached payloads served before the new filter, or
 * items that pass the parent's role check but fail their own).
 *
 * Logic mirrors `checkAuthRequirement` + `hasRequiredRole` in the server
 * route, restricted to the admin/staff branches that matter for the UI.
 */
export function isNavGatePassed(
  gate: { authRequirement?: string; requiredRoles?: string[] | null },
  access: NavAccessState,
): boolean {
  // auth_requirement check
  switch (gate.authRequirement) {
    case 'required':
      if (!access.isAuthenticated) return false;
      break;
    case 'staff':
      if (!access.isStaff) return false;
      break;
    case 'admin':
      if (!access.isAdmin) return false;
      break;
    case 'platform':
      if (!access.isPlatformAdmin) return false;
      break;
    // 'none' | 'optional' | 'citizen' | undefined — no auth-level filter
  }

  // required_roles check (any-match semantics, mirrors server route)
  const required = gate.requiredRoles;
  if (required && required.length > 0) {
    const passes = required.some((role) => {
      if (NAV_ADMIN_ROLE_SLUGS.has(role)) {
        // Any admin slug → user must be admin
        return access.isAdmin;
      }
      // Non-admin role slug — we don't have full slug membership on the
      // client (the centralized hooks only expose isAdmin/isStaff). Fall
      // through to "allow"; the server filter is authoritative for those.
      return true;
    });
    if (!passes) return false;
  }

  return true;
}
